/**
 * Funções para importação de fluxos
 * Valida estrutura, extrai referências e cria fluxo no tenant destino
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Infraestrutura base para export/import
 */

import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import type { FlowExportFormat } from '@/types/flows';
import type { Json } from '@/integrations/supabase/types';
import {
  ExternalReference,
  ExternalReferenceType,
  ReferenceMapping,
  AutoMatchResult,
  REFERENCE_TYPE_CONFIGS,
  isPlaceholder,
} from './reference-types';
import { EXPORT_FORMAT_VERSION } from './flow-export';

/** Logger para módulo de importação */
const log = createLogger('FlowImport');

/**
 * Helper para fazer queries genéricas evitando problemas de tipagem
 * do Supabase client com tabelas muito complexas
 */
async function queryTable<T>(
  table: string,
  selectFields: string,
  filters?: Record<string, unknown>
): Promise<T[]> {
  // Cast para any para evitar erros de tipagem excessivamente complexa
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query = (supabase as any).from(table).select(selectFields);
  
  if (filters) {
    for (const [key, value] of Object.entries(filters)) {
      query = query.eq(key, value);
    }
  }
  
  const { data, error } = await query;
  
  if (error) {
    log.warn(`Erro na query ${table}`, error);
    return [];
  }
  
  return (data || []) as T[];
}

/**
 * Resultado da validação de um arquivo de importação
 */
export interface ImportValidationResult {
  /** Se o arquivo é válido */
  valid: boolean;
  /** Erros encontrados */
  errors: string[];
  /** Avisos (não bloqueantes) */
  warnings: string[];
  /** Dados parseados (se válido) */
  data?: FlowExportFormat;
  /** Referências externas encontradas */
  references?: ExternalReference[];
}

/**
 * Resultado da importação de um fluxo
 */
export interface ImportResult {
  /** Se a importação foi bem-sucedida */
  success: boolean;
  /** ID do fluxo criado */
  flowId?: string;
  /** Erro (se falhou) */
  error?: string;
  /** Referências que não foram mapeadas */
  unmappedReferences: ExternalReference[];
  /** ID do registro de importação */
  importId?: string;
}

/**
 * Lê e valida um arquivo de importação
 */
export async function parseImportFile(file: File): Promise<ImportValidationResult> {
  const errors: string[] = [];
  const warnings: string[] = [];

  try {
    // 1. Verificar extensão do arquivo
    if (!file.name.endsWith('.json') && !file.name.endsWith('.uaizyflow')) {
      errors.push('Formato de arquivo inválido. Use .json ou .uaizyflow');
      return { valid: false, errors, warnings };
    }

    // 2. Verificar tamanho (máximo 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      errors.push('Arquivo muito grande. Tamanho máximo: 10MB');
      return { valid: false, errors, warnings };
    }

    // 3. Ler conteúdo
    const content = await file.text();
    
    // 4. Parsear JSON
    let data: FlowExportFormat;
    try {
      data = JSON.parse(content);
    } catch {
      errors.push('Arquivo JSON inválido. Verifique a estrutura do arquivo.');
      return { valid: false, errors, warnings };
    }

    // 5. Validar estrutura obrigatória
    if (!data.format_version) {
      errors.push('Campo format_version não encontrado');
    }
    if (!data.flow) {
      errors.push('Campo flow não encontrado');
    }
    if (!data.flow?.name) {
      errors.push('Nome do fluxo não encontrado');
    }
    if (!data.flow?.nodes || !Array.isArray(data.flow.nodes)) {
      errors.push('Lista de nodes não encontrada ou inválida');
    }

    if (errors.length > 0) {
      return { valid: false, errors, warnings };
    }

    // 6. Verificar compatibilidade de versão
    const [majorVersion] = data.format_version.split('.');
    const [currentMajor] = EXPORT_FORMAT_VERSION.split('.');
    if (majorVersion !== currentMajor) {
      errors.push(`Versão incompatível: ${data.format_version}. Versão atual: ${EXPORT_FORMAT_VERSION}`);
      return { valid: false, errors, warnings };
    }

    // 7. Extrair e validar referências externas
    const rawReferences = data.external_references || [];
    const references: ExternalReference[] = [];
    
    // Validar e tipar cada referência
    for (const ref of rawReferences) {
      if (!ref.placeholder || !ref.type || !ref.originalId) {
        warnings.push(`Referência incompleta ignorada: ${JSON.stringify(ref)}`);
        continue;
      }
      
      // Validar que o tipo é conhecido
      if (REFERENCE_TYPE_CONFIGS[ref.type as ExternalReferenceType]) {
        references.push({
          placeholder: ref.placeholder,
          type: ref.type as ExternalReferenceType,
          originalId: ref.originalId,
          name: ref.name,
          context: ref.context,
          nodeId: ref.nodeId,
          nodeType: ref.nodeType,
        });
      } else {
        warnings.push(`Tipo de referência desconhecido: ${ref.type}`);
      }
    }

    // 7.1 Deduplicar referências com mesmo placeholder
    // Múltiplos triggers podem referenciar a mesma inbox/departamento
    const uniqueReferences: ExternalReference[] = [];
    const seenPlaceholders = new Map<string, number>();

    for (const ref of references) {
      const existingIndex = seenPlaceholders.get(ref.placeholder);
      if (existingIndex !== undefined) {
        // Combinar contextos na referência existente
        const existing = uniqueReferences[existingIndex];
        existing.context = `${existing.context}; ${ref.context}`;
      } else {
        seenPlaceholders.set(ref.placeholder, uniqueReferences.length);
        uniqueReferences.push({ ...ref }); // Clone para não mutar original
      }
    }

    // 8. Validar tipos de nodes conhecidos
    const unknownNodeTypes: string[] = [];
    for (const node of data.flow.nodes) {
      const nodeType = node.data?.type || node.type;
      // Lista básica de tipos conhecidos (pode ser expandida)
    const knownTypes = [
        'start', 'end', 'message_text', 'message_media', 'message_template',
        'question_buttons', 'question_list', 'input_data', 'condition', 'delay',
        'add_tag', 'remove_tag', 'assign_phase', 'update_contact', 'transfer_human',
        'transfer_department', 'set_variable', 'webhook', 'goto', 'ab_test',
        'filter_array', 'format_array', 'trigger_check', 'pause_bot', 'resume_bot',
        'crm_create_deal', 'crm_change_phase', 'crm_assign_seller', 'business_hours',
        'message_type_check', 'instagram_message',
        // RELEASE: 2.0.0 - Nodes de contrato e cobrança
        'contract_generate', 'charge_generate',
        // RELEASE: 2.2.0 - Node Conexão (Integration Hub)
        'connection_query',
        // RELEASE: 2.1.0 - Node de agrupamento visual
        'group',
      ];
      
      if (!knownTypes.includes(nodeType)) {
        unknownNodeTypes.push(nodeType);
      }
    }

    if (unknownNodeTypes.length > 0) {
      warnings.push(`Tipos de node desconhecidos encontrados: ${unknownNodeTypes.join(', ')}`);
    }

    log.info('Arquivo validado com sucesso', {
      fileName: file.name,
      flowName: data.flow.name,
      nodeCount: data.flow.nodes.length,
      referenceCount: uniqueReferences.length,
    });

    return {
      valid: true,
      errors,
      warnings,
      data,
      references: uniqueReferences,
    };
  } catch (error) {
    log.error('Erro ao processar arquivo', error);
    errors.push(`Erro inesperado: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    return { valid: false, errors, warnings };
  }
}

/**
 * Busca candidatos para uma referência específica no tenant
 * Retorna lista de entidades que podem ser mapeadas
 */
async function fetchCandidatesForType(
  type: ExternalReferenceType, 
  tenantId: string
): Promise<Array<{ id: string; name: string }>> {
  
  try {
    // Para profiles, precisamos buscar via tenant_members
    if (type === 'agent') {
      const members = await queryTable<{
        user_id: string;
        profiles: { id: string; full_name: string };
      }>('tenant_members', 'user_id, profiles!inner(id, full_name)', {
        tenant_id: tenantId,
        is_active: true,
      });

      return members.map(m => ({
        id: m.profiles?.id || m.user_id,
        name: m.profiles?.full_name || '',
      })).filter(c => c.id && c.name);
    }

    // Para sellers CRM
    if (type === 'seller') {
      const data = await queryTable<{ id: string; name: string }>(
        'crm_sellers', 'id, name', { tenant_id: tenantId, is_active: true }
      );
      return data.map(s => ({ id: s.id, name: s.name }));
    }

    // Para departamentos
    if (type === 'department') {
      const data = await queryTable<{ id: string; name: string }>(
        'departments', 'id, name', { tenant_id: tenantId }
      );
      return data.map(d => ({ id: d.id, name: d.name }));
    }

    // Para tags de contato
    if (type === 'contact_tag') {
      const data = await queryTable<{ id: string; name: string }>(
        'contact_tags', 'id, name', { tenant_id: tenantId }
      );
      return data.map(t => ({ id: t.id, name: t.name }));
    }

    // Para inboxes
    // Para inboxes — lista todas exceto estados terminais (suspended, disconnected)
    if (type === 'inbox') {
      const { data, error } = await (supabase as any)
        .from('inboxes')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .not('status', 'in', '("suspended","disconnected")');
      if (error) { log.warn('Erro na query inboxes', error); return []; }
      return (data || []).map((i: any) => ({ id: i.id, name: i.name }));
    }

    // Para workspaces CRM
    if (type === 'workspace') {
      const data = await queryTable<{ id: string; name: string }>(
        'crm_workspaces', 'id, name', { tenant_id: tenantId }
      );
      return data.map(w => ({ id: w.id, name: w.name }));
    }

    // Para pipelines CRM
    if (type === 'pipeline') {
      const data = await queryTable<{ id: string; name: string }>(
        'crm_pipelines', 'id, name', { tenant_id: tenantId }
      );
      return data.map(p => ({ id: p.id, name: p.name }));
    }

    // Para fases CRM — busca via join com crm_pipelines para filtrar por tenant
    if (type === 'phase') {
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, crm_pipelines!inner(tenant_id)')
        .eq('crm_pipelines.tenant_id', tenantId);

      if (error) { log.warn('Erro ao buscar fases CRM (crm_pipeline_phases):', error.message); return []; }
      return (data || []).map((p: any) => ({ id: p.id, name: p.name }));
    }

    // Para filas de distribuição
    if (type === 'distribution_queue') {
      const data = await queryTable<{ id: string; name: string }>(
        'distribution_queues', 'id, name', { tenant_id: tenantId }
      );
      return data.map(q => ({ id: q.id, name: q.name }));
    }

    // Para fases de conversa (kanban)
    if (type === 'kanban_phase') {
      const data = await queryTable<{ id: string; name: string }>(
        'kanban_phases', 'id, name', { tenant_id: tenantId }
      );
      return data.map(p => ({ id: p.id, name: p.name }));
    }

    // Para categorias kanban
    if (type === 'kanban_category') {
      const data = await queryTable<{ id: string; name: string }>(
        'kanban_categories', 'id, name', { tenant_id: tenantId }
      );
      return data.map(c => ({ id: c.id, name: c.name }));
    }

    // Para variáveis do tenant
    // CORREÇÃO: Retornar múltiplos candidatos para auto-match robusto
    // Isso permite match tanto por display_name quanto por name interno
    if (type === 'tenant_variable') {
      const data = await queryTable<{ 
        id: string; 
        name: string; 
        display_name: string | null;
        type: string;
        is_system: boolean;
      }>(
        'tenant_variables', 
        'id, name, display_name, type, is_system', 
        { tenant_id: tenantId }
      );
      
      // Retorna múltiplos nomes para matching:
      // 1. O display_name (nome amigável) - ex: "ID do Negócio"
      // 2. O name interno (se diferente) - ex: "entity_id"
      // Isso garante compatibilidade retroativa com fluxos exportados antes da correção
      return data.flatMap(v => {
        const candidates = [{ id: v.id, name: v.display_name || v.name }];
        // Se display_name existir e for diferente de name, adicionar também o name
        if (v.display_name && v.display_name !== v.name) {
          candidates.push({ id: v.id, name: v.name });
        }
        return candidates;
      });
    }

    // RELEASE: 1.2.0 - 2026-02-03 - Suporte a mídias da galeria interna
    if (type === 'media_gallery') {
      const data = await queryTable<{ id: string; name: string }>(
        'media_gallery',
        'id, name',
        { tenant_id: tenantId }
      );
      return data.map(m => ({ id: m.id, name: m.name }));
    }

    // Para tags de negócio (deal)
    if (type === 'deal_tag') {
      const data = await queryTable<{ id: string; name: string }>(
        'deal_tags', 'id, name', { tenant_id: tenantId }
      );
      return data.map(t => ({ id: t.id, name: t.name }));
    }

    // Para credenciais de webhook/API
    if (type === 'credential') {
      const data = await queryTable<{ id: string; name: string }>(
        'tenant_credentials', 'id, name', { tenant_id: tenantId }
      );
      return data.map(c => ({ id: c.id, name: c.name }));
    }

    // Para templates de WhatsApp
    if (type === 'template') {
      const data = await queryTable<{ id: string; name: string }>(
        'whatsapp_templates', 'id, name', { tenant_id: tenantId }
      );
      return data.map(t => ({ id: t.id, name: t.name }));
    }

    // RELEASE: 2.0.0 - 2026-03-22 - Suporte a contratos publicados
    if (type === 'published_contract') {
      const data = await queryTable<{ id: string; name: string }>(
        'published_contracts', 'id, name', { tenant_id: tenantId }
      );
      return data.map(c => ({ id: c.id, name: c.name }));
    }

    // RELEASE: 2.0.0 - 2026-03-22 - Suporte a meios de pagamento
    if (type === 'meio_pagamento') {
      const data = await queryTable<{ id: string; nome: string }>(
        'financeiro_meios_pagamento', 'id, nome', { tenant_id: tenantId }
      );
      return data.map(m => ({ id: m.id, name: m.nome }));
    }

    // RELEASE: 2.0.0 - 2026-03-22 - Suporte a contas correntes (tabela corrigida: financeiro_conta_corrente)
    if (type === 'conta_corrente') {
      const { data, error } = await (supabase as any)
        .from('financeiro_conta_corrente')
        .select('id, nome')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null);
      if (error) { log.warn('Erro ao buscar contas correntes', error); return []; }
      return (data || []).map((c: { id: string; nome: string }) => ({ id: c.id, name: c.nome }));
    }

    // RELEASE: 2.0.0 - 2026-03-22 - Suporte a produtos CRM
    if (type === 'crm_product') {
      const data = await queryTable<{ id: string; name: string }>(
        'crm_products', 'id, name', { tenant_id: tenantId }
      );
      return data.map(p => ({ id: p.id, name: p.name }));
    }

    return [];
  } catch (error) {
    log.warn(`Erro ao buscar candidatos para ${type}`, error);
    return [];
  }
}

/**
 * Tenta fazer auto-match de referências com entidades do tenant atual
 */
export async function autoMatchReferences(
  references: ExternalReference[],
  tenantId: string
): Promise<AutoMatchResult[]> {
  const results: AutoMatchResult[] = [];

  // Agrupar referências por tipo para otimizar queries
  const refsByType = new Map<ExternalReferenceType, ExternalReference[]>();
  for (const ref of references) {
    if (!refsByType.has(ref.type)) {
      refsByType.set(ref.type, []);
    }
    refsByType.get(ref.type)!.push(ref);
  }

  // Buscar candidatos para cada tipo
  for (const [type, refs] of refsByType) {
    const candidates = await fetchCandidatesForType(type, tenantId);

    for (const ref of refs) {
      const scoredCandidates = candidates
        .map(c => ({
          id: c.id,
          name: c.name,
          similarity: calculateSimilarity(ref.name, c.name),
        }))
        .sort((a, b) => b.similarity - a.similarity);

      results.push({
        reference: ref,
        exactMatch: scoredCandidates.some(c => c.similarity === 1),
        candidates: scoredCandidates.slice(0, 5),
      });
    }
  }

  return results;
}

/**
 * Calcula similaridade entre duas strings (0-1)
 * Usa Levenshtein normalizado
 */
function calculateSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  
  const aLower = a.toLowerCase().trim();
  const bLower = b.toLowerCase().trim();
  
  if (aLower === bLower) return 1;
  
  // Levenshtein simplificado
  const maxLen = Math.max(aLower.length, bLower.length);
  if (maxLen === 0) return 1;
  
  const distance = levenshteinDistance(aLower, bLower);
  return 1 - (distance / maxLen);
}

/**
 * Calcula distância de Levenshtein entre duas strings
 */
function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

/**
 * Interface para metadados de variável no export
 */
export interface VariableMetadata {
  placeholder: string;
  name: string;
  display_name: string;
  type: string;
  is_system: boolean;
  description?: string;
}

/**
 * Opções para importação de fluxo
 */
export interface ImportFlowOptions {
  /** Se deve criar automaticamente variáveis não encontradas */
  autoCreateVariables?: boolean;
}

/**
 * Cria variáveis que não existem no tenant destino
 * 
 * @param references - Lista de referências de variáveis não mapeadas
 * @param tenantId - ID do tenant destino
 * @param exportData - Dados de exportação (contém metadados)
 * @returns Mapa de placeholder -> novo ID criado
 */
export async function createMissingVariables(
  references: ExternalReference[],
  tenantId: string,
  exportData: FlowExportFormat
): Promise<Map<string, string>> {
  const createdMappings = new Map<string, string>();
  
  // Filtrar apenas referências de variáveis
  const variableRefs = references.filter(r => r.type === 'tenant_variable');
  
  if (variableRefs.length === 0) {
    return createdMappings;
  }

  log.info('Criando variáveis faltantes', { 
    count: variableRefs.length,
    tenantId,
  });

  // Buscar metadados do export (se disponível)
  const metadataMap = new Map<string, VariableMetadata>();
  if (exportData.variable_metadata) {
    for (const meta of exportData.variable_metadata) {
      metadataMap.set(meta.placeholder, meta);
    }
  }

  for (const ref of variableRefs) {
    try {
      // Buscar metadados da variável no export
      const metadata = metadataMap.get(ref.placeholder);
      
      // Determinar nome e tipo
      const variableName = metadata?.name || ref.name.replace(/\s+/g, '_').toLowerCase();
      const displayName = metadata?.display_name || ref.name;
      const variableType = metadata?.type || 'text';
      
      // Criar variável no tenant
      const { data, error } = await supabase
        .from('tenant_variables')
        .insert({
          tenant_id: tenantId,
          name: variableName,
          display_name: displayName,
          type: variableType,
          description: `Importado automaticamente do fluxo "${exportData.flow.name}"`,
          is_system: false,
          category: 'custom',
        })
        .select('id')
        .single();

      if (data && !error) {
        createdMappings.set(ref.placeholder, data.id);
        log.info('Variável criada automaticamente', { 
          placeholder: ref.placeholder, 
          newId: data.id,
          name: displayName,
        });
      } else if (error) {
        log.warn('Erro ao criar variável', { 
          placeholder: ref.placeholder, 
          error: error.message,
        });
      }
    } catch (err) {
      log.warn('Erro inesperado ao criar variável', { 
        placeholder: ref.placeholder, 
        error: err,
      });
    }
  }

  log.info('Variáveis criadas', { 
    created: createdMappings.size,
    total: variableRefs.length,
  });

  return createdMappings;
}

/**
 * Cria tags faltantes (contact_tag ou deal_tag) automaticamente no tenant destino
 * @param tagType - Tipo da tag ('contact_tag' ou 'deal_tag')
 * @param references - Lista de referências de tags não mapeadas
 * @param tenantId - ID do tenant destino
 * @returns Mapa de placeholder -> novo ID criado
 */
export async function createMissingTags(
  tagType: ExternalReferenceType,
  references: ExternalReference[],
  tenantId: string,
): Promise<Map<string, string>> {
  const createdMappings = new Map<string, string>();
  
  if (references.length === 0) return createdMappings;

  // Determinar tabela baseado no tipo
  const tableName = tagType === 'contact_tag' ? 'contact_tags' : 'deal_tags';

  log.info(`Criando ${tagType} faltantes`, { 
    count: references.length,
    tenantId,
    table: tableName,
  });

  for (const ref of references) {
    try {
      const tagName = ref.name || ref.placeholder;

      // Verificar se já existe tag com mesmo nome no tenant (evitar duplicata)
      const existing = await queryTable<{ id: string; name: string }>(
        tableName,
        'id, name',
        { tenant_id: tenantId, name: tagName }
      );

      if (existing.length > 0) {
        // Tag já existe, reutilizar
        createdMappings.set(ref.placeholder, existing[0].id);
        log.info(`${tagType} já existe, reutilizando`, { 
          placeholder: ref.placeholder, 
          existingId: existing[0].id,
          name: tagName,
        });
        continue;
      }

      // Criar nova tag
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from(tableName)
        .insert({
          tenant_id: tenantId,
          name: tagName,
        })
        .select('id')
        .single();

      if (data && !error) {
        createdMappings.set(ref.placeholder, data.id);
        log.info(`${tagType} criada automaticamente`, { 
          placeholder: ref.placeholder, 
          newId: data.id,
          name: tagName,
        });
      } else if (error) {
        log.warn(`Erro ao criar ${tagType}`, { 
          placeholder: ref.placeholder, 
          error: error.message,
        });
      }
    } catch (err) {
      log.warn(`Erro inesperado ao criar ${tagType}`, { 
        placeholder: ref.placeholder, 
        error: err,
      });
    }
  }

  log.info(`Tags ${tagType} criadas`, { 
    created: createdMappings.size,
    total: references.length,
  });

  return createdMappings;
}


export async function importFlow(
  exportData: FlowExportFormat,
  tenantId: string,
  mappings: ReferenceMapping[],
  customName?: string,
  options?: ImportFlowOptions
): Promise<ImportResult> {
  try {
    log.info('Iniciando importação', {
      flowName: exportData.flow.name,
      tenantId,
      mappingCount: mappings.length,
      autoCreateVariables: options?.autoCreateVariables,
    });

    // 1. Criar mapa de mapeamentos para lookup rápido
    const mappingMap = new Map<string, string | null>();
    for (const m of mappings) {
      mappingMap.set(m.placeholder, m.mappedId);
    }

    // 1.5. Tratar mapeamentos __auto_create__ para variáveis, contact_tag e deal_tag
    const autoCreateTypes: ExternalReferenceType[] = ['tenant_variable', 'contact_tag', 'deal_tag'];
    const hasAutoCreateMappings = Array.from(mappingMap.values()).some(v => v === '__auto_create__');
    const effectiveAutoCreate = options?.autoCreateVariables || hasAutoCreateMappings;
    
    // Identificar e limpar __auto_create__ do mapa (será tratado como não mapeado para criação)
    for (const [key, value] of mappingMap) {
      if (value === '__auto_create__') {
        mappingMap.set(key, null);
      }
    }

    // 1.6. Criar itens faltantes (variáveis + tags) automaticamente
    if (effectiveAutoCreate) {
      const rawReferences = exportData.external_references || [];
      
      for (const refType of autoCreateTypes) {
        const unmappedRefs = rawReferences
          .filter(r => r.type === refType)
          .filter(r => !mappingMap.has(r.placeholder) || mappingMap.get(r.placeholder) === null)
          .map(r => ({
            placeholder: r.placeholder,
            type: r.type as ExternalReferenceType,
            originalId: r.originalId,
            name: r.name,
            context: r.context,
            nodeId: r.nodeId,
            nodeType: r.nodeType,
          }));

        if (unmappedRefs.length === 0) continue;

        if (refType === 'tenant_variable') {
          log.info('Criando variáveis automaticamente', { count: unmappedRefs.length });
          const createdMappings = await createMissingVariables(unmappedRefs, tenantId, exportData);
          for (const [placeholder, id] of createdMappings) {
            mappingMap.set(placeholder, id);
          }
        } else {
          // Criar tags (contact_tag ou deal_tag)
          log.info(`Criando ${refType} automaticamente`, { count: unmappedRefs.length });
          const createdMappings = await createMissingTags(refType, unmappedRefs, tenantId);
          for (const [placeholder, id] of createdMappings) {
            mappingMap.set(placeholder, id);
          }
        }
      }
    }

    // 2. Aplicar mapeamentos nos nodes
    const mappedNodes = exportData.flow.nodes.map(node => {
      const processedNode = JSON.parse(JSON.stringify(node));
      applyMappingsToObject(processedNode, mappingMap);
      return processedNode;
    });

    // 3. Aplicar mapeamentos nos triggers
    const mappedTriggers = (exportData.flow.trigger_config || []).map(trigger => {
      const processedTrigger = JSON.parse(JSON.stringify(trigger));
      applyMappingsToObject(processedTrigger, mappingMap);
      return processedTrigger;
    });

    // 3.1 Limpar placeholders não mapeados dos triggers (setar como undefined)
    // Isso evita que valores como "__REF:workspace:NomeX__" fiquem salvos e quebrem UI
    cleanUnresolvedTriggerPlaceholders(mappedTriggers);

    // 4. Gerar novos IDs para nodes e edges (evitar colisão)
    const idMap = new Map<string, string>();
    for (const node of mappedNodes) {
      const newId = `node_${crypto.randomUUID().slice(0, 8)}`;
      idMap.set(node.id, newId);
      node.id = newId;

      // Retrocompatibilidade: limpar propriedades internas do ReactFlow v12+
      // que podem estar presentes em arquivos exportados antes da sanitização.
      // IMPORTANTE: GroupNodes precisam preservar width/height pois são containers
      // redimensionáveis (NodeResizer). Sem essas dimensões, o grupo importado
      // renderiza no tamanho mínimo (250x150) e os filhos podem ficar fora do container.
      const isGroupNode = node.type === 'group' || (node.data as any)?.type === 'group';
      delete (node as any).measured;
      delete (node as any).selected;
      delete (node as any).dragging;
      if (!isGroupNode) {
        delete (node as any).width;
        delete (node as any).height;
      }
      delete (node as any).positionAbsolute;
      delete (node as any).internals;
      delete (node as any).resizing;
    }

    // 4.1 Remapear parentId dos nodes filhos de grupos
    // Nodes dentro de grupos possuem parentId apontando para o ID antigo do grupo pai.
    // Sem este remapeamento, os filhos ficam "órfãos" e não renderizam corretamente.
    // Também garante extent: 'parent' para que filhos sigam o grupo ao arrastar.
    const orphanedChildren: string[] = [];
    for (const node of mappedNodes) {
      if (node.parentId && typeof node.parentId === 'string') {
        const newParentId = idMap.get(node.parentId);
        if (newParentId) {
          node.parentId = newParentId;
          (node as any).extent = 'parent';
        } else {
          // Pai não encontrado: promove o filho para o canvas (evita node invisível)
          orphanedChildren.push(node.id);
          delete (node as any).parentId;
          delete (node as any).extent;
        }
      }
    }
    if (orphanedChildren.length > 0) {
      log.warn('[importFlow] Nodes filhos órfãos promovidos ao canvas (parent não encontrado)', {
        orphanedChildren,
      });
    }

    // Atualizar referências de edges
    const mappedEdges = (exportData.flow.edges || []).map(edge => {
      const newEdge = { ...edge };
      newEdge.id = `edge_${crypto.randomUUID().slice(0, 8)}`;
      newEdge.source = idMap.get(edge.source) || edge.source;
      newEdge.target = idMap.get(edge.target) || edge.target;
      return newEdge;
    });

    // Atualizar todas as referências internas de nodes (target_node_id, error_handling, invalid_response)
    updateInternalNodeReferences(mappedNodes, idMap);

    // RELEASE: 1.2.0 - Reconstruir media_url para mídias da galeria após mapeamento
    await reconstructMediaGalleryUrls(mappedNodes, mappingMap);

    // 5. Identificar referências não mapeadas dos nodes
    const rawReferences = exportData.external_references || [];
    const unmappedReferences: ExternalReference[] = rawReferences
      .filter(ref => {
        const mapping = mappingMap.get(ref.placeholder);
        return mapping === null || mapping === undefined;
      })
      .map(ref => ({
        placeholder: ref.placeholder,
        type: ref.type as ExternalReferenceType,
        originalId: ref.originalId,
        name: ref.name,
        context: ref.context,
        nodeId: ref.nodeId,
        nodeType: ref.nodeType,
      }));

    // 5.1 Identificar placeholders não resolvidos nos triggers
    // Isso captura referências que podem não estar no external_references
    const triggerUnmappedRefs = collectUnmappedTriggerReferences(mappedTriggers);
    for (const triggerRef of triggerUnmappedRefs) {
      // Evitar duplicatas (placeholder já registrado)
      if (!unmappedReferences.some(r => r.placeholder === triggerRef.placeholder)) {
        unmappedReferences.push(triggerRef);
        log.warn('Placeholder de trigger não resolvido', {
          placeholder: triggerRef.placeholder,
          type: triggerRef.type,
          context: triggerRef.context,
        });
      }
    }

    // 6. Criar fluxo no banco
    const flowName = customName?.trim() || `${exportData.flow.name} (importado)`;
    
    const { data: newFlow, error: flowError } = await supabase
      .from('flows')
      .insert({
        tenant_id: tenantId,
        name: flowName,
        description: exportData.flow.description || null,
        nodes: mappedNodes as unknown as Json,
        edges: mappedEdges as unknown as Json,
        variables: (exportData.flow.variables || []) as unknown as Json,
        settings: (exportData.flow.settings || {}) as unknown as Json,
        trigger_config: mappedTriggers as unknown as Json,
        status: 'draft', // Sempre como rascunho para revisão
      })
      .select('id')
      .single();

    if (flowError || !newFlow) {
      log.error('Erro ao criar fluxo', flowError);
      return {
        success: false,
        error: flowError?.message || 'Erro ao criar fluxo',
        unmappedReferences,
      };
    }

    // 7. Registrar importação
    const { data: importRecord } = await supabase
      .from('flow_template_imports')
      .insert({
        target_tenant_id: tenantId,
        target_flow_id: newFlow.id,
        reference_mapping: Object.fromEntries(
          mappings.map(m => [m.placeholder, m.mappedId])
        ) as unknown as Json,
        unmapped_references: unmappedReferences as unknown as Json,
        import_status: 'completed',
      })
      .select('id')
      .single();

    log.info('Importação concluída com sucesso', {
      flowId: newFlow.id,
      importId: importRecord?.id,
      unmappedCount: unmappedReferences.length,
    });

    return {
      success: true,
      flowId: newFlow.id,
      importId: importRecord?.id,
      unmappedReferences,
    };
  } catch (error) {
    log.error('Erro inesperado na importação', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro inesperado',
      unmappedReferences: [],
    };
  }
}

/**
 * Aplica mapeamentos recursivamente em um objeto
 */
function applyMappingsToObject(
  obj: Record<string, unknown>,
  mappings: Map<string, string | null>
): void {
  for (const key of Object.keys(obj)) {
    const value = obj[key];

    if (typeof value === 'string' && isPlaceholder(value)) {
      const mappedValue = mappings.get(value);
      if (mappedValue !== undefined && mappedValue !== null) {
        obj[key] = mappedValue;
      }
      // Se não mapeado, mantém o placeholder (será identificado como warning)
    } else if (Array.isArray(value)) {
      obj[key] = value.map(item => {
        if (typeof item === 'string' && isPlaceholder(item)) {
          const mapped = mappings.get(item);
          return mapped !== undefined && mapped !== null ? mapped : item;
        } else if (typeof item === 'object' && item !== null) {
          applyMappingsToObject(item as Record<string, unknown>, mappings);
          return item;
        }
        return item;
      });
    } else if (typeof value === 'object' && value !== null) {
      applyMappingsToObject(value as Record<string, unknown>, mappings);
    }
  }
}

/**
 * Atualiza referências internas de nodes em estruturas aninhadas
 * Campos que referenciam outros nodes por ID precisam ser remapeados
 * após a geração de novos IDs durante a importação
 * 
 * Estruturas afetadas:
 * - target_node_id: goto node (presente em vários tipos)
 * - error_handling: webhook (input_node_id, exceeded_goto_node_id)
 * - invalid_response: question_buttons, question_list, input_data (goto_node_id, exceeded_goto_node_id)
 * 
 * @param nodes - Array de nodes com configurações a serem atualizadas
 * @param idMap - Mapa de IDs antigos para novos IDs
 */
function updateInternalNodeReferences(
  nodes: Array<Record<string, unknown>>,
  idMap: Map<string, string>
): void {
  for (const node of nodes) {
    const data = node.data as Record<string, unknown> | undefined;
    const config = data?.config as Record<string, unknown> | undefined;
    if (!config) continue;

    // 1. Atualizar target_node_id (goto node) - centralizado aqui
    if (typeof config.target_node_id === 'string') {
      const newId = idMap.get(config.target_node_id);
      if (newId) {
        config.target_node_id = newId;
        log.debug(`[updateInternalNodeReferences] target_node_id remapeado: ${config.target_node_id} -> ${newId}`);
      }
    }

    // 2. Webhook: error_handling
    const errorHandling = config.error_handling as Record<string, unknown> | undefined;
    if (errorHandling) {
      // input_node_id: node para retry de input
      if (typeof errorHandling.input_node_id === 'string') {
        const newId = idMap.get(errorHandling.input_node_id);
        if (newId) {
          log.debug(`[updateInternalNodeReferences] error_handling.input_node_id remapeado: ${errorHandling.input_node_id} -> ${newId}`);
          errorHandling.input_node_id = newId;
        }
      }
      // exceeded_goto_node_id: node para quando excede tentativas
      if (typeof errorHandling.exceeded_goto_node_id === 'string') {
        const newId = idMap.get(errorHandling.exceeded_goto_node_id);
        if (newId) {
          log.debug(`[updateInternalNodeReferences] error_handling.exceeded_goto_node_id remapeado: ${errorHandling.exceeded_goto_node_id} -> ${newId}`);
          errorHandling.exceeded_goto_node_id = newId;
        }
      }
    }

    // 3. Question Buttons/List/Input Data: invalid_response
    const invalidResponse = config.invalid_response as Record<string, unknown> | undefined;
    if (invalidResponse) {
      // goto_node_id: node para resposta inválida
      if (typeof invalidResponse.goto_node_id === 'string') {
        const newId = idMap.get(invalidResponse.goto_node_id);
        if (newId) {
          log.debug(`[updateInternalNodeReferences] invalid_response.goto_node_id remapeado: ${invalidResponse.goto_node_id} -> ${newId}`);
          invalidResponse.goto_node_id = newId;
        }
      }
      // exceeded_goto_node_id: node para quando excede tentativas
      if (typeof invalidResponse.exceeded_goto_node_id === 'string') {
        const newId = idMap.get(invalidResponse.exceeded_goto_node_id);
        if (newId) {
          log.debug(`[updateInternalNodeReferences] invalid_response.exceeded_goto_node_id remapeado: ${invalidResponse.exceeded_goto_node_id} -> ${newId}`);
          invalidResponse.exceeded_goto_node_id = newId;
        }
      }
    }
  }
}

/**
 * Coleta placeholders não resolvidos dos triggers importados
 * Percorre os campos mapeados em TRIGGER_REFERENCE_FIELDS e detecta 
 * valores que ainda contêm o formato __REF:type:name__
 * 
 * @param triggers - Array de trigger configs após tentativa de mapeamento
 * @returns Array de referências não mapeadas para exibir como warnings
 */
/**
 * Definição dos campos de trigger que contêm referências externas
 * Usado para limpeza e coleta de warnings
 * 
 * RELEASE: 1.3.0 - 2026-02-03 - Extraído para reutilização entre funções
 */
const TRIGGER_REFERENCE_FIELDS_CONFIG = [
  { path: 'inbox_ids', type: 'inbox' as ExternalReferenceType, isArray: true },
  { path: 'workspace_id', type: 'workspace' as ExternalReferenceType, isArray: false },
  { path: 'pipeline_id', type: 'pipeline' as ExternalReferenceType, isArray: false },
  { path: 'phase_ids', type: 'phase' as ExternalReferenceType, isArray: true },
  { path: 'seller_id', type: 'seller' as ExternalReferenceType, isArray: false },
  { path: 'assigned_to', type: 'agent' as ExternalReferenceType, isArray: false },
  { path: 'deal_tag_ids', type: 'deal_tag' as ExternalReferenceType, isArray: true },
  { path: 'department_id', type: 'department' as ExternalReferenceType, isArray: false },
  { path: 'department_ids', type: 'department' as ExternalReferenceType, isArray: true },
  { path: 'kanban_category_id', type: 'kanban_category' as ExternalReferenceType, isArray: false },
  { path: 'kanban_phase_ids', type: 'kanban_phase' as ExternalReferenceType, isArray: true },
  { path: 'contact_tag_ids', type: 'contact_tag' as ExternalReferenceType, isArray: true },
  { path: 'tag_ids', type: 'contact_tag' as ExternalReferenceType, isArray: true },
];

/**
 * Limpa placeholders não resolvidos dos triggers
 * Após aplicar mapeamentos, campos que ainda contêm __REF:...__ são limpos para undefined
 * Isso evita que a UI mostre campos vazios sem aviso (Select não reconhece placeholder)
 * 
 * RELEASE: 1.3.0 - 2026-02-03 - Correção de tratativa de campos de gatilho não mapeados
 * 
 * @param triggers - Array de trigger configs já processados com applyMappingsToObject
 */
function cleanUnresolvedTriggerPlaceholders(
  triggers: Array<Record<string, unknown>>
): void {
  for (const trigger of triggers) {
    for (const field of TRIGGER_REFERENCE_FIELDS_CONFIG) {
      const value = trigger[field.path];
      
      if (field.isArray && Array.isArray(value)) {
        // Filtrar placeholders não resolvidos de arrays
        const filtered = value.filter(
          item => !(typeof item === 'string' && isPlaceholder(item))
        );
        
        // Se array ficou vazio, remover o campo
        if (filtered.length === 0) {
          trigger[field.path] = undefined;
        } else if (filtered.length !== value.length) {
          // Alguns itens foram removidos
          trigger[field.path] = filtered;
          log.debug('Placeholders não mapeados removidos de array', {
            field: field.path,
            originalCount: value.length,
            newCount: filtered.length,
          });
        }
      } else if (typeof value === 'string' && isPlaceholder(value)) {
        // Limpar valor único não mapeado
        trigger[field.path] = undefined;
        log.debug('Placeholder não mapeado limpo para undefined', {
          field: field.path,
          originalValue: value,
        });
      }
    }
  }
}

/**
 * Coleta placeholders não resolvidos dos triggers importados
 * Percorre os campos mapeados em TRIGGER_REFERENCE_FIELDS_CONFIG e detecta 
 * valores que ainda contêm o formato __REF:type:name__
 * 
 * RELEASE: 1.0.0 - 2026-02-03 - Implementação inicial
 * RELEASE: 1.3.0 - 2026-02-03 - Warnings com contexto melhorado (label e obrigatoriedade)
 * 
 * @param triggers - Array de trigger configs após tentativa de mapeamento
 * @returns Array de referências não mapeadas para exibir como warnings
 */
function collectUnmappedTriggerReferences(
  triggers: Array<Record<string, unknown>>
): ExternalReference[] {
  const unmappedRefs: ExternalReference[] = [];

  for (const trigger of triggers) {
    const triggerType = (trigger.type as string) || 'unknown';
    
    for (const field of TRIGGER_REFERENCE_FIELDS_CONFIG) {
      const value = trigger[field.path];
      // Buscar configuração do tipo para obter label e required
      const typeConfig = REFERENCE_TYPE_CONFIGS[field.type];
      const fieldLabel = typeConfig?.label || field.path;
      const isRequired = typeConfig?.required || false;
      
      if (field.isArray && Array.isArray(value)) {
        // Verificar cada item do array
        for (const item of value) {
          if (typeof item === 'string' && isPlaceholder(item)) {
            // Extrair nome do placeholder
            const match = item.match(/^__REF:[a-z_]+:(.+)__$/);
            const name = match ? match[1] : field.type;
            
            unmappedRefs.push({
              placeholder: item,
              type: field.type,
              originalId: 'unknown', // Não temos acesso ao ID original aqui
              name,
              context: `Gatilho "${triggerType}" - ${fieldLabel} (campo ${isRequired ? 'obrigatório' : 'opcional'})`,
            });
          }
        }
      } else if (typeof value === 'string' && isPlaceholder(value)) {
        // Verificar valor único
        const match = value.match(/^__REF:[a-z_]+:(.+)__$/);
        const name = match ? match[1] : field.type;
        
        unmappedRefs.push({
          placeholder: value,
          type: field.type,
          originalId: 'unknown',
          name,
          context: `Gatilho "${triggerType}" - ${fieldLabel} (campo ${isRequired ? 'obrigatório' : 'opcional'})`,
        });
      }
    }
  }
  
  return unmappedRefs;
}

/**
 * Reconstrói a media_url para nodes message_media que usam galeria interna
 * Após o placeholder ser substituído pelo UUID real, busca o storage_path
 * e reconstrói a URL pública do arquivo no tenant destino
 * 
 * RELEASE: 1.2.0 - 2026-02-03 - Suporte a mídias da galeria
 * 
 * @param nodes - Array de nodes já mapeados
 * @param mappings - Mapa de placeholder -> UUID mapeado
 */
async function reconstructMediaGalleryUrls(
  nodes: Array<Record<string, unknown>>,
  mappings: Map<string, string | null>
): Promise<void> {
  // Coletar IDs de mídias mapeadas que precisam de reconstrução
  const mediaIdsToFetch: string[] = [];
  const nodesToUpdate: Array<{ node: Record<string, unknown>; mediaId: string }> = [];

  for (const node of nodes) {
    const data = node.data as Record<string, unknown> | undefined;
    const config = data?.config as Record<string, unknown> | undefined;
    const nodeType = data?.type || node.type;

    // Verificar se é node de mídia da galeria com URL que precisa reconstrução
    if (
      nodeType === 'message_media' &&
      config?.media_source === 'gallery' &&
      config?.media_url === '__INTERNAL_MEDIA__' &&
      typeof config?.media_gallery_id === 'string' &&
      !isPlaceholder(config.media_gallery_id as string)
    ) {
      const mediaId = config.media_gallery_id as string;
      mediaIdsToFetch.push(mediaId);
      nodesToUpdate.push({ node, mediaId });
    }
  }

  if (mediaIdsToFetch.length === 0) {
    return;
  }

  log.debug('Reconstruindo URLs de mídia da galeria', { count: mediaIdsToFetch.length });

  try {
    // Buscar storage_path de todas as mídias de uma vez
    const { data: mediaRecords, error } = await supabase
      .from('media_gallery')
      .select('id, storage_path')
      .in('id', mediaIdsToFetch);

    if (error) {
      log.warn('Erro ao buscar storage_path das mídias', { error });
      return;
    }

    // Criar mapa id -> storage_path
    const storagePathMap = new Map<string, string>();
    for (const record of mediaRecords || []) {
      storagePathMap.set(record.id, record.storage_path);
    }

    // Atualizar cada node com a URL reconstruída
    for (const { node, mediaId } of nodesToUpdate) {
      const data = node.data as Record<string, unknown>;
      const config = data.config as Record<string, unknown>;
      const storagePath = storagePathMap.get(mediaId);

      if (storagePath) {
        // Reconstruir URL usando helper de storage
        // Import dinâmico para evitar dependência circular
        const supabaseUrl = 'https://qxuavopibmaijhvfpzzl.supabase.co';
        const publicUrl = `${supabaseUrl}/storage/v1/object/public/media/${storagePath}`;
        
        config.media_url = publicUrl;
        log.debug('URL de mídia reconstruída', { 
          mediaId, 
          storagePath,
          newUrl: publicUrl,
        });
      } else {
        log.warn('Storage path não encontrado para mídia', { mediaId });
        // Deixar media_url como __INTERNAL_MEDIA__ para indicar problema
      }
    }

    log.info('URLs de mídia da galeria reconstruídas', { 
      total: nodesToUpdate.length,
      success: nodesToUpdate.filter(n => {
        const config = (n.node.data as Record<string, unknown>).config as Record<string, unknown>;
        return config.media_url !== '__INTERNAL_MEDIA__';
      }).length,
    });
  } catch (err) {
    log.error('Erro inesperado ao reconstruir URLs de mídia', { error: err });
  }
}
